// One authenticated session; socket packets are hints. HTTP + durable cursors
// reconcile authoritative state, including edits/deletes missed while offline.
export default class ChatSession {
  constructor({ request, socket, actorKey, storage, storageScope = '', onInbox = () => {}, hidden = () => false, schedule = (callback, delay) => setTimeout(callback, delay), cancel = (timer) => clearTimeout(timer), uuid = () => crypto.randomUUID() }) {
    Object.assign(this, { request, socket, actorKey, storage, onInbox, hidden, schedule, cancel, uuid });
    this.streams = new Map(); this.closed = false; this.failures = 0;
    this.draftKey = `chat-v2:drafts:${storageScope}:${actorKey}`;
    try { this.drafts = JSON.parse(storage.getItem(this.draftKey) || '[]'); } catch (_) { this.drafts = []; }
    this.drafts = Array.isArray(this.drafts) ? this.drafts.map((d) => ({ ...d, status: 'Pending — retry' })) : [];
    this.inboxTimer = null;
    this.inbox = () => {
      if (this.inboxTimer || this.closed || this.hidden()) return;
      this.inboxTimer = this.schedule(() => { this.inboxTimer = null; if (!this.closed && !this.hidden()) this.onInbox(); }, 250);
    };
    this.onChanged = ({ stream_key: key } = {}) => this.refresh(key);
    this.onConnect = () => { for (const key of this.streams.keys()) this.join(key); this.inbox(); };
    this.onLost = ({ stream_key: key, status } = {}) => {
      const stream = this.streams.get(key); if (stream) { stream.status = status === 403 ? 'Access denied' : 'Reconnecting'; this.notify(stream); }
    };
    socket.on('changed', this.onChanged);
    socket.on('connect', this.onConnect);
    socket.on('inbox_changed', this.inbox);
    socket.on('subscription_lost', this.onLost);
    this.onDisconnect = () => { for (const s of this.streams.values()) { s.status = 'Reconnecting'; this.notify(s); } };
    socket.on('disconnect', this.onDisconnect);
    socket.on('session_unavailable', this.onDisconnect);
    const reconcileInbox = () => {
      if (this.closed) return;
      this.inbox(); this.reconcileTimer = this.schedule(reconcileInbox, 60000);
    };
    this.reconcileTimer = this.schedule(reconcileInbox, 60000);
  }
  path(key) { const [provider, id] = key.split(':'); return `chat-v2/${provider}/${id}`; }
  saveDrafts() { if (!this.discarded) this.storage.setItem(this.draftKey, JSON.stringify(this.drafts)); }
  clearDrafts() { this.discarded = true; this.drafts = []; this.storage.removeItem(this.draftKey); }
  notify(s) {
    if (this.closed || !this.streams.has(s.key)) return;
    const messages = [...s.messages.values()].sort((a, b) => Number(a.id) - Number(b.id));
    const state = { messages, status: s.status, lastSynced: s.lastSynced, hasMore: Boolean(s.before), drafts: this.drafts.filter((d) => d.key === s.key) };
    s.listeners.forEach((listener) => listener(state));
  }
  watch(key, listener) {
    let s = this.streams.get(key);
    if (!s) { s = { key, messages: new Map(), cursor: null, before: null, status: 'Connecting', listeners: new Set(), failures: 0 }; this.streams.set(key, s); }
    s.listeners.add(listener); this.notify(s); this.join(key); this.refresh(key);
    return () => {
      s.listeners.delete(listener);
      if (!s.listeners.size) { this.cancel(s.timer); this.streams.delete(key); this.socket.emit('unsubscribe', key); }
    };
  }
  join(key) {
    if (!this.socket.connected) return;
    const [provider, id] = key.split(':');
    this.socket.emit('subscribe', { provider, conversation_id: Number(id) }, (result) => {
      const s = this.streams.get(key); if (!s) return;
      if (result?.ok) this.refresh(key);
      else { s.status = result?.status === 403 ? 'Access denied' : 'Reconnecting'; this.notify(s); }
    });
  }
  refresh(key, older = false) {
    const s = this.streams.get(key);
    if (!s || this.closed) return Promise.resolve();
    if (s.flight) { s.pending = true; s.olderPending = s.olderPending || older; return s.flight; }
    this.cancel(s.timer);
    if (this.hidden()) return Promise.resolve();
    if (!this.socket.connected && !this.socket.active && this.socket.connect) this.socket.connect();
    s.flight = (async () => {
      try {
        const query = older && s.before ? `?before=${s.before}` : s.cursor === null ? '' : `?after=${s.cursor}`;
        let response;
        try { response = await this.request(`${this.path(key)}/history${query}`, 'GET'); }
        catch (error) {
          if (error.response?.data?.code !== 'CHAT_RESYNC_REQUIRED') throw error;
          response = await this.request(`${this.path(key)}/history`, 'GET');
          older = false;
        }
        if (this.closed || this.streams.get(key) !== s) return;
        const data = response.data;
        if (data.messages) {
          if (!older) s.messages.clear();
          for (const m of data.messages) s.messages.set(String(m.id), m);
          s.before = data.before;
          if (!older) s.cursor = data.cursor;
        } else {
          const events = data.events || [];
          if (events.some((event, index) => event.seq !== s.cursor + index + 1) || data.cursor !== s.cursor + events.length) {
            s.cursor = null;
            throw new Error('Non-contiguous chat recovery');
          }
          for (const event of events) {
            if (event.message) s.messages.set(String(event.message.id), event.message);
            else if (event.message_id) s.messages.delete(String(event.message_id));
          }
          s.cursor = data.cursor;
          if (data.has_more) s.pending = true;
        }
        s.failures = 0; s.lastSynced = Date.now(); s.status = this.socket.connected ? 'Connected' : 'Connected via refresh';
        this.notify(s);
      } catch (error) {
        if (this.streams.get(key) !== s || this.closed) return;
        s.failures = Math.min(s.failures + 1, 4);
        s.status = error.response?.status === 403 ? 'Access denied' : error.response?.status === 401 ? 'Sign in required' : 'Offline — retrying';
        this.notify(s);
      } finally {
        s.flight = null;
        if (this.streams.get(key) === s && !this.closed) {
          const delay = s.pending ? 0 : Math.min(60000, (this.socket.connected ? 30000 : 5000) * (2 ** s.failures));
          const olderPending = s.olderPending; s.olderPending = false;
          s.pending = false;
          s.timer = this.schedule(() => this.refresh(key, olderPending), delay + (delay ? Math.floor(Math.random() * 1000) : 0));
        }
      }
    })();
    return s.flight;
  }
  resume() { for (const key of this.streams.keys()) this.refresh(key); this.inbox(); }
  async read(key, id) {
    const s = this.streams.get(key);
    if (!s || this.hidden() || !id || Number(id) <= (s.readThrough || 0)) return;
    await this.request(`${this.path(key)}/read`, 'POST', { through_message_id: Number(id) });
    s.readThrough = Number(id);
  }
  async send(key, payload, existingId) {
    if (this.closed || this.discarded) throw new Error('Chat session is closed');
    const id = existingId || this.uuid();
    let draft = this.drafts.find((d) => d.id === id && d.key === key);
    if (!draft) { draft = { id, key, payload, status: 'Sending' }; this.drafts.push(draft); }
    if (draft.flight) return draft.flight;
    draft.status = 'Sending';
    const s = this.streams.get(key);
    try { this.saveDrafts(); }
    catch (error) { draft.status = 'Draft storage unavailable — retry'; if (s) this.notify(s); throw error; }
    if (s) this.notify(s);
    const flight = this.request(`${this.path(key)}/messages`, 'POST', { ...draft.payload, client_message_id: id })
      .then(async ({ data }) => {
        this.drafts = this.drafts.filter((d) => d !== draft);
        // The server already accepted the message. A storage cleanup failure
        // must not turn that acknowledgement into a false send failure.
        try { this.saveDrafts(); } catch (_) { /* A restored old UUID is safe to retry. */ }
        if (s && this.streams.get(key) === s) { s.messages.set(String(data.id), data); this.notify(s); await this.refresh(key); }
        return data;
      }).catch((error) => { draft.status = 'Failed — retry'; try { this.saveDrafts(); } catch (_) { /* Retain the in-memory retry. */ } if (s) this.notify(s); throw error; })
      .finally(() => { delete draft.flight; });
    Object.defineProperty(draft, 'flight', { value: flight, writable: true, configurable: true, enumerable: false });
    return flight;
  }
  close() {
    this.closed = true;
    this.cancel(this.inboxTimer); this.cancel(this.reconcileTimer);
    for (const s of this.streams.values()) this.cancel(s.timer);
    this.streams.clear();
    this.socket.off('changed', this.onChanged); this.socket.off('connect', this.onConnect);
    this.socket.off('inbox_changed', this.inbox); this.socket.off('subscription_lost', this.onLost);
    this.socket.off('disconnect', this.onDisconnect); this.socket.off('session_unavailable', this.onDisconnect);
    this.socket.disconnect();
  }
}
